import { useCallback, useEffect, useRef, useState } from "react";

function buildHeaderMenuItems({ isMac, isLeftPanelCollapsed, isRightPanelCollapsed }) {
    return {
        file: [
            { id: "create-file", label: "새 파일" },
            { id: "create-folder", label: "새 폴더" },
            { id: "upload", label: "업로드" },
            { id: "download-source-zip", label: "소스(zip) 다운로드" },
        ],
        edit: [
            { id: "save", label: "저장", shortcut: isMac ? "Cmd+S" : "Ctrl+S" },
            { id: "undo", label: "실행 취소", shortcut: isMac ? "Cmd+Z" : "Ctrl+Z" },
            { id: "redo", label: "다시 실행", shortcut: isMac ? "Cmd+Shift+Z" : "Ctrl+Shift+Z" },
            { id: "search", label: "찾기", shortcut: isMac ? "Cmd+F" : "Ctrl+F" },
        ],
        view: [
            { id: "toggle-left-panel", label: isLeftPanelCollapsed ? "좌측 패널 열기" : "좌측 패널 닫기" },
            { id: "toggle-right-panel", label: isRightPanelCollapsed ? "우측 패널 열기" : "우측 패널 닫기" },
            { id: "show-pdf", label: "PDF" },
            { id: "show-history", label: "히스토리" },
            { id: "show-review", label: "리뷰" },
        ],
    };
}

function getHelpMenuPosition(anchorElement, direction = "bottom") {
    if (!anchorElement) {
        return null;
    }

    const rect = anchorElement.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 40;
    const top =
        direction === "right"
            ? Math.min(window.innerHeight - menuHeight - 8, Math.max(8, rect.top))
            : Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6);
    const left =
        direction === "right"
            ? Math.min(window.innerWidth - menuWidth - 8, rect.right + 6)
            : Math.min(window.innerWidth - menuWidth - 8, Math.max(8, rect.left));

    return { top, left };
}

function getHeaderMenuPosition(anchorElement) {
    if (!anchorElement) {
        return null;
    }

    const rect = anchorElement.getBoundingClientRect();
    const menuWidth = 188;
    const menuHeight = 220;
    const top = Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6);
    const left = Math.min(window.innerWidth - menuWidth - 8, Math.max(8, rect.left));

    return { top, left };
}

export function useManuscriptHeaderMenus({
    createNode,
    dispatchEditorCommand,
    downloadSourceZip,
    fileInputRef,
    flushCurrentDocument,
    isLeftPanelCollapsed,
    isMac,
    isRightPanelCollapsed,
    setIsLeftPanelCollapsed,
    setIsRightPanelCollapsed,
    setRightPanelTab,
} = {}) {
    const [helpMenuPosition, setHelpMenuPosition] = useState(null);
    const [headerMenuState, setHeaderMenuState] = useState(null);
    const helpMenuRef = useRef(null);
    const headerMenuRef = useRef(null);
    const headerMenuItems = buildHeaderMenuItems({
        isMac,
        isLeftPanelCollapsed,
        isRightPanelCollapsed,
    });

    const closeHelpMenu = useCallback(() => {
        setHelpMenuPosition(null);
    }, []);

    const closeHeaderMenu = useCallback(() => {
        setHeaderMenuState(null);
    }, []);

    const closeHeaderMenus = useCallback(() => {
        setHelpMenuPosition(null);
        setHeaderMenuState(null);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target?.closest?.("[data-manuscript-header-menu-trigger='true']")) {
                return;
            }
            if (!helpMenuRef.current?.contains(event.target)) {
                closeHelpMenu();
            }
            if (!headerMenuRef.current?.contains(event.target)) {
                closeHeaderMenu();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [closeHeaderMenu, closeHelpMenu]);

    const openHelpMenu = useCallback(
        (anchorElement, direction = "bottom") => {
            const position = getHelpMenuPosition(anchorElement, direction);
            if (!position) return;
            setHelpMenuPosition(position);
            closeHeaderMenu();
        },
        [closeHeaderMenu],
    );

    const openHeaderMenu = useCallback(
        (menuKey, anchorElement) => {
            const position = getHeaderMenuPosition(anchorElement);
            if (!position) return;
            setHeaderMenuState((prev) =>
                prev?.key === menuKey
                    ? null
                    : {
                          key: menuKey,
                          ...position,
                      },
            );
            closeHelpMenu();
        },
        [closeHelpMenu],
    );

    const runHeaderMenuAction = useCallback(
        async (action) => {
            closeHeaderMenu();

            if (action === "create-file") {
                await createNode?.("text");
                return;
            }
            if (action === "create-folder") {
                await createNode?.("folder");
                return;
            }
            if (action === "upload") {
                fileInputRef?.current?.click();
                return;
            }
            if (action === "download-source-zip") {
                await downloadSourceZip?.();
                return;
            }
            if (action === "save") {
                await flushCurrentDocument?.();
                return;
            }
            if (action === "undo" || action === "redo" || action === "search") {
                dispatchEditorCommand?.(action);
                return;
            }
            if (action === "toggle-left-panel") {
                setIsLeftPanelCollapsed?.((prev) => !prev);
                return;
            }
            if (action === "toggle-right-panel") {
                setIsRightPanelCollapsed?.((prev) => !prev);
                return;
            }
            if (action === "show-pdf") {
                setRightPanelTab?.("pdf");
                setIsRightPanelCollapsed?.(false);
                return;
            }
            if (action === "show-history") {
                setRightPanelTab?.("history");
                setIsRightPanelCollapsed?.(false);
                return;
            }
            if (action === "show-review") {
                setRightPanelTab?.("review");
                setIsRightPanelCollapsed?.(false);
            }
        },
        [
            closeHeaderMenu,
            createNode,
            dispatchEditorCommand,
            downloadSourceZip,
            fileInputRef,
            flushCurrentDocument,
            setIsLeftPanelCollapsed,
            setIsRightPanelCollapsed,
            setRightPanelTab,
        ],
    );

    return {
        closeHeaderMenu,
        closeHeaderMenus,
        closeHelpMenu,
        headerMenuItems,
        headerMenuRef,
        headerMenuState,
        helpMenuPosition,
        helpMenuRef,
        openHeaderMenu,
        openHelpMenu,
        runHeaderMenuAction,
    };
}
